import { yupResolver } from "@hookform/resolvers/yup";
import { PlusInSquareIcon, TrashAltIcon } from "assets/icons";
import { FilesUploader } from "components";
import { MediaDirectoryKeys } from "constants/enums";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Button,
  FormField,
  IconButton,
  Input,
  MediaFilePicker,
  ReachTextEditor,
  Switch
} from "ui";

import { QuizQuestionItem } from "../../../content.types";
import { quizItemSchema } from "../../../helpers";
import { QuizItemFormProps } from "./QuizItemForm.types";

import styles from "./QuizItemForm.module.scss";

const QuizItemForm = ({
  formId,
  defaultValues,
  onSubmit
}: QuizItemFormProps) => {
  const { t } = useTranslation();
  const { control, formState, watch, register, handleSubmit, setValue } =
    useForm<QuizQuestionItem>({
      resolver: yupResolver(quizItemSchema),
      defaultValues
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "answers"
  });

  const answersWatch = watch("answers");

  const handleCorrectAnswerChange = (index: number, value: boolean) => {
    if (value) {
      fields.forEach((_, i) => {
        if (i !== index) {
          setValue(`answers.${i}.isCorrect`, false);
        }
      });
    }
  };

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      <Input
        {...register("question")}
        errorMessage={formState.errors.question?.message}
        label="Question"
        placeholder="Enter Question"
      />

      <Controller
        control={control}
        name="media"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <FormField label="Media (optional)" errorMessage={error?.message}>
            <FilesUploader
              uploadDirectoryKey={MediaDirectoryKeys.Admin}
              onSuccessUploading={(media) => {
                setValue("mediaId", media.id);
                onChange(media);
              }}
            >
              {(uploaderProps) => (
                <MediaFilePicker
                  {...uploaderProps}
                  value={value ?? null}
                  customSize={{ width: 188, height: 106 }}
                  onDeleteMediaFile={() => {
                    setValue("mediaId", null);
                    onChange(null);
                  }}
                />
              )}
            </FilesUploader>
          </FormField>
        )}
      />

      <Controller
        control={control}
        name="description"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <FormField
            label="Description (optional)"
            errorMessage={error?.message}
          >
            <ReachTextEditor
              defaultValue={value}
              onChange={(nextValue) => {
                onChange(nextValue || null);
              }}
            />
          </FormField>
        )}
      />

      {fields.map((field, index) => (
        <div key={field.id} className={styles.answer}>
          <Input
            {...register(`answers.${index}.answer`)}
            errorMessage={formState.errors.answers?.[index]?.answer?.message}
            label={`${t("Option")} ${index + 1}`}
            placeholder="Enter an answer"
            containerClassName={styles.answer_inputContainer}
          />

          <Controller
            control={control}
            name={`answers.${index}.isCorrect`}
            render={({ field: { value, onChange } }) => (
              <FormField
                label={index === 0 ? "Correct" : " "}
                labelClassName={styles.answer_emptyLabel}
                containerClassName={styles.answer_emptyFieldContainer}
              >
                <div className={styles.answer_switch}>
                  <Switch
                    checked={value}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleCorrectAnswerChange(index, e.target.checked);
                        onChange(e);
                      }
                    }}
                  />
                </div>
              </FormField>
            )}
          />
          <FormField
            label=" "
            labelClassName={styles.answer_emptyLabel}
            containerClassName={styles.answer_emptyFieldContainer}
          >
            <div className={styles.answer_deleteButton}>
              <IconButton
                icon={<TrashAltIcon fill="#ff0033" width={24} height={24} />}
                disabled={fields.length < 3 || answersWatch[index].isCorrect}
                onClick={() => remove(index)}
              />
            </div>
          </FormField>
        </div>
      ))}

      {fields.length < 5 && (
        <Button
          variant="default"
          className={styles.form_submit}
          icon={<PlusInSquareIcon />}
          type="button"
          onClick={() => append({ answer: "", isCorrect: false })}
        >
          {t("Add option")}
        </Button>
      )}
    </form>
  );
};

export default QuizItemForm;
